import { ElementRef } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import {
  personalDataContentId,
  personalDataStepperCorrectionDelayMs,
  personalDataStepperMaxLayoutAttempts,
  personalDataStepperTopPadding,
} from './personal-data-form.config';

type StepScrollOptions = {
  stepper: MatStepper;
  stepperContainer: ElementRef<HTMLElement>;
  selectedIndex: number;
  firstControlId: string;
  isDesktop: boolean;
};

export function scrollPersonalDataStepIntoView(options: StepScrollOptions): void {
  tryScrollPersonalDataStep(options, 0);
}

export function focusPersonalDataStepControl(
  stepperContainer: ElementRef<HTMLElement>,
  controlId: string
): HTMLElement | null {
  const control = getStepElement(stepperContainer, controlId);

  if (!control) {
    return null;
  }

  control.focus({ preventScroll: true });
  return control;
}

function tryScrollPersonalDataStep(options: StepScrollOptions, attempt: number): void {
  requestAnimationFrame(() => {
    if (options.stepper.selectedIndex !== options.selectedIndex) {
      return;
    }

    const firstControl = getStepElement(options.stepperContainer, options.firstControlId);
    const stepHeader = getSelectedStepHeader(options.stepperContainer, options.selectedIndex);
    const firstControlReady = firstControl instanceof HTMLElement && firstControl.getClientRects().length > 0;
    const headerReady = options.isDesktop || !!stepHeader?.getClientRects().length;

    if (!firstControlReady || !headerReady) {
      if (attempt < personalDataStepperMaxLayoutAttempts) {
        tryScrollPersonalDataStep(options, attempt + 1);
      }

      return;
    }

    scrollStepIntoContent(stepHeader, firstControl, true);
  });
}

function getSelectedStepHeader(stepperContainer: ElementRef<HTMLElement>, selectedIndex: number): HTMLElement | null {
  const headers = stepperContainer.nativeElement.querySelectorAll<HTMLElement>('.mat-vertical-stepper-header');
  return headers.item(selectedIndex) || null;
}

function getStepElement(stepperContainer: ElementRef<HTMLElement>, elementId: string): HTMLElement | null {
  return stepperContainer.nativeElement.querySelector<HTMLElement>(`#${elementId}`) || null;
}

function scrollStepIntoContent(
  stepHeader: HTMLElement | null,
  firstControl: HTMLElement,
  correctAfterLayout: boolean
): void {
  const content = document.getElementById(personalDataContentId);
  const topElement = stepHeader || firstControl;

  if (!content) {
    topElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
    return;
  }

  const contentRect = content.getBoundingClientRect();
  const topElementRect = topElement.getBoundingClientRect();
  const targetTop = content.scrollTop + topElementRect.top - contentRect.top - personalDataStepperTopPadding;

  content.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });

  if (!correctAfterLayout) {
    return;
  }

  setTimeout(() => {
    if (!firstControl.closest('.mat-vertical-content-container-active')) {
      return;
    }

    scrollStepIntoContent(stepHeader, firstControl, false);
  }, personalDataStepperCorrectionDelayMs);
}
